import { getEnvByHostname } from '../utils/env';
import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Select, Button, Space, message, Row, Col, Divider, InputNumber, Tooltip, Tag } from 'antd';
import { WalletOutlined, ReloadOutlined, BankOutlined, SwapOutlined, MobileOutlined, CreditCardOutlined } from '@ant-design/icons';
import ReactJson from '@microlink/react-json-view';
import { getChannelLogo, buildChannelOptions } from '../utils/channelLogos';
import { testApi } from '../services/api';
import { testTemplates, buildRequestBody, generateWithdrawOrderId } from '../utils/testTemplates';
import { useLanguage } from '../contexts/LanguageContext';
import { usePageCache } from '../contexts/PageCacheContext';

const WITHDRAW_METHOD_CONFIG = {
  BANK_TRANSFER: { icon: <BankOutlined />,      color: 'blue',    label: 'Bank Transfer' },
  PIX_CODE:      { icon: <SwapOutlined />,       color: 'green',   label: 'PIX' },
  INTERAC:       { icon: <SwapOutlined />,       color: 'purple',  label: 'Interac' },
  EFT:           { icon: <BankOutlined />,       color: 'cyan',    label: 'EFT' },
  LINKAJA:       { icon: <MobileOutlined />,     color: 'red',     label: 'LinkAja' },
  OVO:           { icon: <MobileOutlined />,     color: 'volcano', label: 'OVO' },
  GOPAY:         { icon: <MobileOutlined />,     color: 'geekblue',label: 'GoPay' },
  DANA:          { icon: <MobileOutlined />,     color: 'blue',    label: 'DANA' },
  SHOPEEPAY:     { icon: <CreditCardOutlined />, color: 'orange',  label: 'ShopeePay' },
};

const withdrawMethodOption = (value) => {
  const cfg = WITHDRAW_METHOD_CONFIG[value] || {};
  return {
    value,
    label: (
      <Space size={6}>
        <Tag icon={cfg.icon} color={cfg.color} style={{ margin: 0, fontSize: 12 }}>{cfg.label || value}</Tag>
      </Space>
    ),
  };
};

export default function Payout() {
  const [form] = Form.useForm();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [userChannels, setUserChannels] = useState([]);
  const [currentChannelIndex, setCurrentChannelIndex] = useState(0);
  const [userCards, setUserCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const { cache, save: saveCache } = usePageCache('payout');

  useEffect(() => {
    loadUserChannels();
    loadUserCards();
    // 恢复缓存
    if (cache) {
      if (cache.result) setResult(cache.result);
      if (cache.selectedChannel) setSelectedChannel(cache.selectedChannel);
      if (cache.currentChannelIndex != null) setCurrentChannelIndex(cache.currentChannelIndex);
      if (cache.formValues) form.setFieldsValue(cache.formValues);
    } else {
      const savedData = localStorage.getItem('payoutFormData');
      if (savedData) { try { const d = JSON.parse(savedData); form.setFieldsValue(d); if (d.channelCode) setSelectedChannel(d.channelCode); } catch (e) {} }
    }
  }, [form]);

  // 页面卸载时保存缓存
  useEffect(() => {
    return () => {
      saveCache({ result, selectedChannel, currentChannelIndex, formValues: form.getFieldsValue() });
    };
  });

  const loadUserChannels = async () => {
    try {
      const { data } = await testApi.getChannelCurrencies();
      const map = data.data?.responseBody || data.data || {};
      const channels = Object.keys(map).map(channelCode => ({ channelCode, currencies: [...new Set(map[channelCode] || [])] }));
      setUserChannels(channels);
      if (channels.length > 0) {
        form.setFieldsValue({ channelCode: channels[0].channelCode, currency: channels[0].currencies[0] });
        setSelectedChannel(channels[0].channelCode);
      }
    } catch (e) { setUserChannels([]); }
  };

  const loadUserCards = async () => {
    const uid = localStorage.getItem('uid') || localStorage.getItem('username');
    if (!uid) return;
    setCardsLoading(true);
    try {
      const { data } = await testApi.getUserCards(uid);
      const res = data.data?.responseBody;
      let parsed = res;
      if (typeof res === 'string') {
        try { parsed = JSON.parse(res); } catch (e) { parsed = null; }
      }
      if (parsed?.data && Array.isArray(parsed.data)) {
        setUserCards(parsed.data);
      } else if (Array.isArray(parsed)) {
        setUserCards(parsed);
      } else {
        setUserCards([]);
      }
    } catch (error) {
      console.error('Failed to load user cards:', error);
      setUserCards([]);
    } finally {
      setCardsLoading(false);
    }
  };

  const saveFormData = () => localStorage.setItem('payoutFormData', JSON.stringify(form.getFieldsValue()));

  const handleChannelChange = (channelCode) => {
    setSelectedChannel(channelCode);
    const idx = userChannels.findIndex(c => c.channelCode === channelCode);
    if (idx >= 0) {
      setCurrentChannelIndex(idx);
      form.setFieldsValue({ currency: userChannels[idx].currencies[0] });
    }
    form.setFieldsValue({ externalOrderId: generateWithdrawOrderId(channelCode), recipientId: undefined });
    saveFormData();
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const requestBody = buildRequestBody('PAYOUT', values, values.channelCode, values.currency);
      const response = await testApi.executeTest({
        testType: 'PAYOUT', channelCode: values.channelCode,
        businessPartner: values.businessPartner || 'DEMO_ACCOUNT',
        currency: values.currency, env: values.env || getEnvByHostname(),
        executor: localStorage.getItem('username'), requestBody: JSON.stringify(requestBody)
      });
      const res = response.data;
      if (res.data?.responseBody && typeof res.data.responseBody === 'string') {
        try { res.data.responseBody = JSON.parse(res.data.responseBody); } catch (e) {}
      }
      setResult(res);
      const rbMsg = res.data?.responseBody?.msg;
      if (res.code === '200') message.success(rbMsg || t('payoutSuccess'));
      else message.warning(`${t('payoutFailed')}: ${rbMsg || res.msg || t('failed')}`);
    } catch (error) {
      message.error(t('payoutFailed') + ': ' + (error.response?.data?.msg || error.message));
      setResult(error.response?.data || { error: error.message });
    } finally { setLoading(false); }
  };

  const renderExtParamFields = () => {
    if (!selectedChannel) return null;
    const fields = (testTemplates.PAYOUT?.channels[selectedChannel] || []).filter(f => f.group === 'extParam');
    if (!fields.length) return null;
    return (
      <>
        <Divider style={{ margin: '16px 0' }} />
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: '#374151' }}>⚙️ {t('extParams')}</div>
        <Row gutter={[16, 16]}>
          {fields.map(field => (
            <Col xs={24} sm={field.name === 'recipientId' ? 24 : 12} key={field.name}>
              <Form.Item
                label={<Space size={4}><span>{t(field.name) || field.label}</span>{field.tip && <Tooltip title={t(field.tip)}><span style={{ cursor: 'help', color: '#1890ff', fontSize: 12 }}>?</span></Tooltip>}</Space>}
                name={field.name} rules={[{ required: field.required }]} initialValue={field.default}>
                {field.name === 'recipientId'
                  ? <Select
                      placeholder={t('pleaseSelect', 'Recipient ID')}
                      loading={cardsLoading}
                      showSearch
                      optionFilterProp="label"
                      popupMatchSelectWidth={false}
                      style={{ width: '100%' }}
                      notFoundContent={cardsLoading ? t('loading') : t('noCardData')}
                      options={userCards
                        .filter(card => card.status === 'SUCCESS' && (!selectedChannel || card.channelCode === selectedChannel))
                        .map(card => ({
                          label: `${card.recipientId} · ${card.accountName || ''} · ${card.currency} · ${card.status}`,
                          value: card.recipientId
                        }))}
                    />
                  : field.type === 'select'
                    ? <Select placeholder={field.placeholder} options={field.name === 'withdrawMethod'
                        ? field.options.map(withdrawMethodOption)
                        : field.options.map(o => ({ label: o, value: o }))} />
                    : <Input placeholder={field.placeholder} />}
              </Form.Item>
            </Col>
          ))}
        </Row>
      </>
    );
  };

  return (
    <div style={{ padding: '16px 24px', height: 'calc(100vh - 64px)', overflow: 'auto' }}>
      <div className="fade-in-up">
        <div style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', padding: '12px 24px', borderRadius: '8px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)' }}>
          <Space size={12}>
            <WalletOutlined style={{ color: '#fff', fontSize: 20 }} />
            <span style={{ fontSize: 18, fontWeight: 600, color: '#fff' }}>{t('payout')} {userChannels.length > 0 && `- ${getChannelLogo(userChannels[currentChannelIndex]?.channelCode).name}`}</span>
          </Space>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => { form.resetFields(); setResult(null); setSelectedChannel(null); }} type="text" style={{ color: '#fff' }}>{t('reset')}</Button>
            {userChannels.length > 1 && (
              <Button onClick={() => {
                const next = (currentChannelIndex + 1) % userChannels.length;
                setCurrentChannelIndex(next);
                form.setFieldsValue({ channelCode: userChannels[next].channelCode, currency: userChannels[next].currencies[0] });
                setSelectedChannel(userChannels[next].channelCode);
              }} type="text" style={{ color: '#fff' }}>{t('switchChannel')}</Button>
            )}
          </Space>
        </div>

        <Row gutter={16}>
          <Col xs={24} lg={12}>
            <Card className="modern-card" bodyStyle={{ padding: '24px' }}>
              <Form form={form} layout="vertical" onFinish={handleSubmit} onValuesChange={saveFormData}>
                <Form.Item name="businessPartner" hidden><Input /></Form.Item>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: '#374151' }}>⚙️ {t('basicConfig')}</div>
                <Form.Item label={t('channel')} name="channelCode" rules={[{ required: true }]}>
                  <Select placeholder={t('selectChannel')} onChange={handleChannelChange}
                    options={buildChannelOptions(userChannels.map(c => c.channelCode))} />
                </Form.Item>
                <Form.Item label={t('currency')} name="currency" rules={[{ required: true }]} initialValue="USD">
                  <Select options={(userChannels.find(c => c.channelCode === form.getFieldValue('channelCode'))?.currencies || []).map(c => ({ label: c, value: c }))} />
                </Form.Item>
                <Divider style={{ margin: '16px 0' }} />
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: '#374151' }}>💸 {t('payoutParams')}</div>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label={t('amount')} name="amount" rules={[{ required: true }]}>
                      <InputNumber style={{ width: '100%' }} placeholder={t('amount')} min={0} step={0.01} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label={t('withdrawMethod')} name="withdrawMethod" rules={[{ required: true }]}>
                      <Select placeholder={t('withdrawMethod')} options={['BANK_TRANSFER', 'PIX_CODE', 'INTERAC', 'EFT', 'LINKAJA', 'OVO', 'GOPAY', 'DANA', 'SHOPEEPAY'].map(withdrawMethodOption)} />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item label={t('callbackUrl')} name="callbackUrl" initialValue="https://test-platform.fiat.com/callback/payout">
                  <Input disabled style={{ background: '#f5f5f5', color: '#666' }} />
                </Form.Item>
                {renderExtParamFields()}
                <Button type="primary" htmlType="submit" icon={<WalletOutlined />} loading={loading} size="large" block className="primary-button" style={{ height: 48, marginTop: 16 }}>
                  {loading ? t('payingOut') : t('submitPayout')}
                </Button>
              </Form>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card className="modern-card" title={`📋 ${t('payoutResult')}`} bodyStyle={{ padding: '24px' }}>
              {result ? (
                <div className="json-viewer"><ReactJson src={result} collapsed={false} theme="rjv-default" displayDataTypes={false} displayObjectSize={false} enableClipboard={true} /></div>
              ) : (
                <div style={{ padding: 60, textAlign: 'center', color: '#999', background: '#f9fafb', borderRadius: 8, border: '2px dashed #d1d5db' }}>
                  <WalletOutlined style={{ fontSize: 48, marginBottom: 16, color: '#d1d5db' }} />
                  <div>{t('submitToShowPayoutResult')}</div>
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
}
